import { useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Box } from '@mui/material';
import { common } from '@mui/material/colors';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useAdvanceStore, useFilterStore, useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';
import * as utils from '/src/utils';

/**
 * List Advances Page
 */
export const ListAdvancesPage: React.FC = observer(() => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;

  /** Context **/
  const advanceStore = useAdvanceStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [search, setSearch] = useState<Record<string, string>>({});
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const advances = advanceStore.advances;
  const isFiltered = !!Object.values(search).length;

  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);

  const listData = useMemo(() => {
    if (advances) {
      let list = advances.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.companyId) list = list.filter((advance) => advance.company._id === search.companyId);

      return list;
    }
  }, [advances, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    advanceStore.query();
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Advance}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { propName: 'name', label: 'Advance Name' },
        { flex: 15, propName: 'company.name', label: 'Company' },
        { flex: 10, propName: 'loan.name', label: 'Deal' },
        { propName: 'amount', label: 'Amount', textAlign: 'right' },
        { propName: 'dateRequested', label: 'Date Requested', textAlign: 'right' },
        { propName: 'dateFulfilled', label: 'Date Fulfilled', textAlign: 'right' },
        { propName: 'originationFee', label: 'Origination Fee', textAlign: 'right' },
      ]}
      noFilteredDataMessage="No Advances found for that Company."
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.companyId}
                  updateValue={(id) => setSearch({ ...search, companyId: id })}
                  options={companies
                    .filter((company) => !company.isLender)
                    .map((company) => ({ label: company.name, value: company._id }))}
                  placeholder="All Companies"
                  enablePlaceholderOption
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
        </>
      }
      isFiltered={isFiltered}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((advance, i) => (
        <ListRow
          key={advance._id}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !advance.notes.length,
              showNotesPopup: () => showNotesPopup(advance.notes, advance.name),
            },
            { propName: 'name', content: advance.name, url: `/advances/${advance._id}` },
            {
              propName: 'company.name',
              content: advance.company.name,
              url: `/companies/${advance.company._id}`,
              flex: 15,
            },
            {
              propName: 'loan.name',
              content: advance.loan.name,
              url: `/deals/${advance.loan._id}`,
              flex: 10,
            },
            { propName: 'amount', content: utils.currency.format(advance.amount), textAlign: 'right' },
            { propName: 'dateRequested', content: dayjs(advance.dateRequested).format(mediumDate), textAlign: 'right' },
            { propName: 'dateFulfilled', content: dayjs(advance.dateFulfilled).format(mediumDate), textAlign: 'right' },
            {
              propName: 'originationFee',
              content: utils.currency.format(advance.originationFee ?? 0),
              textAlign: 'right',
            },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
